import { readonly, ref } from "vue"
import { logger } from "@/services/logger"

/**
 * Notices that a newer frontend has been deployed while this tab was open.
 *
 * The problem it solves: `index.html` names content-hashed asset files, so a tab that loaded
 * before a deployment keeps running the old release until the document itself is fetched again.
 * Correct cache headers (see `nginx.conf`) make the *next* full page load pick up the new release;
 * this module is what turns "the next full page load" into something that actually happens —
 * either because the user accepts the prompt, or because the router swaps the release in silently
 * on their next navigation (see `router/index.js`).
 *
 * Deliberately not a Pinia store: the router guard reads this outside of any component, and the
 * state here is a single flag with no server data behind it.
 */

/** Build manifest emitted into the bundle root by the `lewara:version-manifest` plugin. */
const MANIFEST_URL = "/version.json"

/** Matches the notification centre's cadence — a ~60 byte poll, so the interval can stay short. */
const POLL_INTERVAL_MS = 5 * 60 * 1000

/** The build this tab is running, compiled in by `vite.config.ts`. */
const runningVersion: string | undefined = import.meta.env.VITE_VERSION

const updatePending = ref(false)

// Module-level rather than reactive state: timer handles and listeners are not data.
let pollTimer: ReturnType<typeof setInterval> | null = null
let visibilityListener: (() => void) | null = null

/** True once a different version is live on the server. Reactive; for templates. */
export const updateAvailable = readonly(updatePending)

/** Non-reactive read, for call sites outside a component (the router guard). */
export const isUpdatePending = () => updatePending.value

async function checkForUpdate() {
  try {
    // `no-store` covers well-behaved caches; the query parameter also defeats an intermediate
    // proxy that ignores the header, which would otherwise pin this tab to a stale answer forever.
    const response = await fetch(`${MANIFEST_URL}?t=${Date.now()}`, { cache: "no-store" })
    if (!response.ok) return

    // A deployment that predates the manifest — or the SPA fallback answering with index.html —
    // lands here as a JSON parse error and is swallowed with everything else below.
    const { version } = await response.json()
    if (!version || version === runningVersion) return

    logger.log(`New frontend version deployed: ${runningVersion} -> ${version}`)
    updatePending.value = true
    stopVersionCheck()
  } catch {
    // Offline, or the container is mid-swap and briefly unreachable. Both are expected and
    // self-correcting: the next tick asks again. Never surface this to the user.
  }
}

/**
 * Begins watching for new deployments. A no-op outside production builds, where there is no
 * manifest to poll, and in any build without a version to compare against — without a known
 * running version every answer would look like a new release and reload the app in a loop.
 */
export function startVersionCheck() {
  if (!import.meta.env.PROD || !runningVersion) return
  if (pollTimer) return

  checkForUpdate()
  pollTimer = setInterval(checkForUpdate, POLL_INTERVAL_MS)

  // Background tabs have their timers throttled heavily, so a tab left open over a lunch break
  // would otherwise learn about a deployment minutes after the user is back looking at it.
  visibilityListener = () => {
    if (document.visibilityState === "visible") checkForUpdate()
  }
  document.addEventListener("visibilitychange", visibilityListener)
}

export function stopVersionCheck() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
  if (visibilityListener) {
    document.removeEventListener("visibilitychange", visibilityListener)
    visibilityListener = null
  }
}

/**
 * Loads the new release. `reload()` re-requests the document, which revalidates against the server
 * and returns the new `index.html` with its new asset URLs.
 */
export function applyUpdate() {
  window.location.reload()
}
